import { TFunction } from 'i18next';
import ControllerException, { ControllerExceptionCodes } from './controllerException';
import { safeStringify } from './stringify';

export default class AppError {
  code?: string;

  message: string;

  debug?: string;

  stack?: string;

  serverStack?: string;

  fromApi: boolean;

  apiLogId?: number;

  data?: ControllerException;

  constructor(message: string, code?: string, debug?: string, stack?: string) {
    this.message = message;
    this.code = code;
    this.debug = debug;
    this.stack = stack;
    this.fromApi = false;
  }

  isEqual(error: AppError | undefined): boolean {
    if (error === undefined) return false;
    if (this.message !== error.message) return false;
    if (this.code !== error?.code) return false;
    if (this.debug !== error?.debug) return false;
    if (this.fromApi !== error.fromApi) return false;

    return true;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static fromOutlookError(error: any): AppError {
    const ae = new AppError(error.message ? error.message : 'Unknown error');

    ae.code = error.code ? error.code.toString() : error.errorCode ? error.errorCode.toString() : '';
    ae.stack = error.stack ? error.stack : '';
    ae.debug = error.body ? error.body : '';

    return ae;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static fromGraphError(error: any): AppError {
    // Microsoft Graph and SharePoint surface their structured errors through
    // the axios response body, not on the Error object itself. The shape is:
    //   error.response.data = { error: { code: "itemNotFound", message: "..." } }
    // The top-level `error.code` that axios attaches is its own transport
    // code ("ERR_BAD_REQUEST" / similar) which is useless to downstream
    // routing in getGraphSharepointErrorMsg -- that function matches on the
    // Graph code ("itemNotFound", "resourceLocked", etc.). Pull the inner
    // envelope so callers see the Graph code instead of the axios string.
    const graphError = error?.response?.data?.error;
    if (graphError) {
      const ae = new AppError(graphError.message ?? 'Unknown error');
      ae.code = graphError.code ?? '';
      ae.stack = error.stack ?? '';
      ae.debug = safeStringify(error.response.data);

      return ae;
    }

    // Fallback: the caller passed a non-axios Graph error directly -- e.g. the
    // MS Graph SDK's error object, or a helper that already unwrapped the
    // response. Keep the previous behavior for those paths.
    const ae = new AppError(error.message ? error.message : 'Unknown error');
    ae.code = error.code ? error.code.toString() : error.errorCode ? error.errorCode.toString() : '';
    ae.stack = error.stack ? error.stack : '';
    ae.debug = error.body ? error.body : '';

    return ae;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static fromApiError(error: any): AppError {
    if (error instanceof AppError) return error;

    const ae = new AppError(error.message ? error.message : 'Unknown error');

    if (error.isAxiosError && error.response) {
      if (error.response.data) {
        let responseMessage = '';

        // Get debug info
        if (typeof error.response.data === 'string') {
          responseMessage = error.response.data;
        } else {
          responseMessage = safeStringify(error.response.data);
        }
        ae.debug = responseMessage || '';

        const isObjectBody =
          typeof error.response.data === 'object' && error.response.data !== null && !Array.isArray(error.response.data);

        // If the response data contains a ControllerException, set it
        if (isObjectBody && 'code' in error.response.data) {
          ae.data = error.response.data;
        }

        // A problem-details body (title, status and detail, such as the 409 of a ConflictException) carries the
        // text for the user in detail; its code, when set, stays readable in data for the scenes that branch on it
        const isProblemDetails =
          isObjectBody &&
          'title' in error.response.data &&
          'status' in error.response.data &&
          typeof error.response.data.detail === 'string';

        // create message from response data and extract stack trace if available
        const stackIdx = responseMessage.lastIndexOf('StackTrace:');
        if (isProblemDetails) {
          ae.data = error.response.data;
          ae.message = error.response.data.detail;
        } else if (stackIdx > 0) {
          ae.message = responseMessage.substring(0, stackIdx);
          ae.message = ae.message.replaceAll(/\r?\n|\r/g, ' '); //replace newlines with spaces
          ae.serverStack = responseMessage.substring(stackIdx);
        } else {
          ae.message = responseMessage;
        }

        // Extract log ID if available
        const logIdx = responseMessage.lastIndexOf('LogId:');
        if (logIdx >= 0) {
          ae.apiLogId = Number.parseInt(responseMessage.substring(logIdx + 6).trim());
        }
      }
      // set code and client stack trace
      ae.code = error.response.status ? error.response.status.toString() : '';
      ae.stack = error.stack ? error.stack : '';
      //403 - not authenticated and 404 - not found
      //these are returned by the API but not logged because ASP.NET blocks the call before we can log.
      //-> treat this as a client-side error so it will be send to the server for logging.
      ae.fromApi = ae.code !== '403' && ae.code !== '404';
    } else {
      // If it's not an Axios error or no data is returned treat it as a generic error
      ae.code = error.code ? error.code.toString() : '';
      ae.stack = error.stack ? error.stack : '';
      ae.debug = error.debug ? error.debug : '';
      ae.fromApi = error.code ? true : false; //when no code is provided, treat like a client side error
    }

    return ae;
  }

  /**
   * A request made with responseType 'blob' (a file download) gets its error body as a Blob
   * too, which hides the ControllerException the API put in it: fromApiError only reads an
   * object with a code. Read the body back first, then classify as usual.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static async fromBlobApiError(error: any): Promise<AppError> {
    const body = error?.response?.data;

    if (body instanceof Blob) {
      const text = await body.text();
      let data: unknown = text;

      try {
        data = JSON.parse(text);
      } catch {
        // keep the text when it is not JSON
      }

      error.response.data = data;
    }

    return AppError.fromApiError(error);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static fromAuthError(error: any): AppError {
    const ae = new AppError(error.message ? error.message : 'Unknown error');

    ae.code = error.code ? error.code.toString() : error.errorCode ? error.errorCode.toString() : '';
    ae.stack = error.stack ? error.stack : '';
    ae.debug = error.body ? error.body : '';

    return ae;
  }

  static isControllerException(error: unknown): boolean {
    return (
      error instanceof AppError &&
      error.data?.code !== undefined &&
      Object.values(ControllerExceptionCodes).includes(error.data.code as ControllerExceptionCodes)
    );
  }

  static getControllerExceptionCode(error: unknown): ControllerExceptionCodes | undefined {
    if (AppError.isControllerException(error)) {
      return (error as AppError).data?.code as ControllerExceptionCodes;
    }

    return undefined;
  }

  static getControllerExceptionMsg = (error: unknown, t: TFunction<string[]>): string | undefined => {
    if (AppError.isControllerException(error)) {
      const dto = (error as AppError).data;
      if (dto?.code) {
        // Pass dto.message through so codes whose i18n templates interpolate it
        // (e.g. INTEGRATION0001 with the missing permission name) render correctly.
        return ControllerException.getMessage(dto.code as ControllerExceptionCodes, t, dto.message);
      }
    }

    return undefined;
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const normalizeError = (error: string | any): AppError | undefined => {
  if (!error) {
    return undefined;
  }
  let normalizedError: AppError;

  //when the error is already an AppError, return that
  if (error instanceof AppError) {
    normalizedError = error;
  } else if (typeof error === 'string') {
    //for raw strings, split on | and create a new AppError
    const errParts = error.split('|');
    normalizedError = errParts.length > 1 ? new AppError(errParts[1], '', errParts[0]) : new AppError(error);
  } else {
    //else, create a new AppError and try to add properties like message, code and stack
    normalizedError = new AppError(
      error.message,
      error.code ? error.code : '',
      safeStringify(error),
      error.stack ? error.stack : '',
    );
  }

  return normalizedError;
};
